import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

interface OutlineContextType {
  activeId: string | null;
  setActiveId: (id: string) => void;
  registerIntersectionObserver: (
    id: string,
    element: HTMLElement,
    threshold?: number
  ) => void;
  unregisterIntersectionObserver: (id: string) => void;
}

const OutlineContext = createContext<OutlineContextType | undefined>(undefined);

export const useOutline = () => {
  const context = useContext(OutlineContext);
  if (!context) {
    throw new Error("useOutline must be used within an OutlineProvider");
  }
  return context;
};

interface OutlineProviderProps {
  children: React.ReactNode;
  rootMargin?: string;
}

export const OutlineProvider: React.FC<OutlineProviderProps> = ({
  children,
  rootMargin = "-50% 0px -50% 0px",
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const elementsMap = useRef<Map<HTMLElement, string>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Stable observer callback
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      let topMostElement: HTMLElement | null = null;
      let topMostId: string | null = null;
      let minTop = Infinity;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const rect = element.getBoundingClientRect();
          if (rect.top < minTop) {
            minTop = rect.top;
            topMostElement = element;
            topMostId = elementsMap.current.get(element) || null;
          }
        }
      });

      if (topMostId !== activeIdRef.current) {
        activeIdRef.current = topMostId;
        setActiveId(topMostId);
      }
    },
    []
  );

  // Lazily initialize observer in registerIntersectionObserver
  const registerIntersectionObserver = useCallback(
    (id: string, element: HTMLElement) => {
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(observerCallback, {
          rootMargin,
        });
      }
      elementsMap.current.set(element, id);
      observerRef.current.observe(element);
    },
    [observerCallback, rootMargin]
  );

  const unregisterIntersectionObserver = useCallback((id: string) => {
    for (const [element, elementId] of elementsMap.current.entries()) {
      if (elementId === id) {
        if (observerRef.current) {
          observerRef.current.unobserve(element);
        }
        elementsMap.current.delete(element);
        break;
      }
    }
  }, []);

  // Cleanup observer on unmount
  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    []
  );

  return (
    <OutlineContext.Provider
      value={{
        activeId,
        setActiveId,
        registerIntersectionObserver,
        unregisterIntersectionObserver,
      }}
    >
      {children}
    </OutlineContext.Provider>
  );
};

export default OutlineProvider;
